import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ModLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  // Build 94b — efter token-expiry redirectas modder hit med ?returnTo=...
  // För att de ska komma tillbaka till sin pflegemask. Endast interna mod-paths accepteras.
  const [search] = useSearchParams();
  const rawReturnTo = search.get("returnTo") || "";
  const safeReturnTo = rawReturnTo.startsWith("/mod") && !rawReturnTo.startsWith("//") ? rawReturnTo : "/mod";

  const emailTypoHint = /@vidalv\.life\b/i.test(email.trim());
  const emailSuggestion = email.trim().replace(/@vidalv\.life\b/i, "@vidalv.live");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem("mod_token", data.token);
      sessionStorage.setItem("mod_user", JSON.stringify(data.user));
      navigate(safeReturnTo);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Inloggningen misslyckades");
    }
    setLoading(false);
  };

  return (
    <div data-testid="mod-login-page" className="max-w-md mx-auto px-4 py-20">
      <h1 className="font-heading text-2xl font-medium text-ink mb-2">Inloggning för redaktionen</h1>
      <p className="text-sm text-muted-foreground mb-4">Intern vy för vidalv.live-teamet.</p>

      {/* Build 94b — visa banner när moderatorn har skickats hit pga utgången session */}
      {rawReturnTo && (
        <div data-testid="mod-login-returnto-banner" className="bg-amber-50 border border-amber-300 rounded-sm p-3 mb-5 text-xs text-bark leading-relaxed">
          Din förra session gick ut. Eventuella osparade ändringar ligger kvar lokalt i webbläsaren och återställs när du har loggat in igen.
        </div>
      )}

      <div className="bg-bone-alt/60 border border-warm-border rounded-sm p-4 mb-6 text-xs text-bark leading-relaxed">
        <p className="font-semibold text-ink mb-1">Två typer av konton:</p>
        <ul className="flex flex-col gap-1 mb-3">
          <li><span className="inline-block w-14 font-semibold text-falu">Master</span> &ndash; full åtkomst till allt (platser, bilder, vardagskort, användare m.m.). Redaktionens huvudkonto.</li>
          <li><span className="inline-block w-14 font-semibold text-pine">Moderator</span> &ndash; får redigera anslag, evenemang, föreningar och företag.</li>
        </ul>
        <p className="text-[11px] text-bark/75 italic">
          Nytt co-moderatorkonto läggs upp av Master under <strong>Admin &rarr; Användare</strong>. Master skickar sedan inloggningsuppgifterna personligen (mejl eller muntligt) till personen i bygden.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* E-post med öga */}
        <div>
          <label className="block text-base font-medium text-ink mb-1">E-post</label>
          <div className="relative">
            <input
              data-testid="mod-email"
              type={showEmail ? "text" : "email"}
              name="email"
              autoComplete="email"
              placeholder="redaktion@vidalv.live"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full min-h-[56px] px-4 py-3 pr-12 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none"
            />
            <button
              type="button"
              data-testid="toggle-email-visibility"
              onClick={() => setShowEmail((v) => !v)}
              aria-label={showEmail ? "Dölj e-post" : "Visa e-post"}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-bark hover:text-ink transition-colors"
            >
              {showEmail ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {emailTypoHint && (
            <button
              type="button"
              onClick={() => setEmail(emailSuggestion)}
              data-testid="email-typo-hint"
              className="mt-2 text-xs text-falu hover:text-falu-hover text-left"
            >
              Menade du <strong>{emailSuggestion}</strong>? (domänen är <code>.live</code>, inte <code>.life</code>) – klicka för att rätta.
            </button>
          )}
        </div>

        {/* Lösenord med öga */}
        <div>
          <label className="block text-base font-medium text-ink mb-1">Lösenord</label>
          <div className="relative">
            <input
              data-testid="mod-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full min-h-[56px] px-4 py-3 pr-12 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none"
            />
            <button
              type="button"
              data-testid="toggle-password-visibility"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Dölj lösenord" : "Visa lösenord"}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-bark hover:text-ink transition-colors"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        {error && (
          <p data-testid="mod-login-error" className="text-falu text-base">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="mod-login-button"
          className="bg-pine hover:bg-pine-hover text-white font-body font-medium text-lg px-6 py-4 rounded-md transition-colors disabled:opacity-50"
        >
          {loading ? "Loggar in..." : "Logga in"}
        </button>
      </form>
    </div>
  );
}
